"use client";

import { GhostBlock } from "@faire/retailer-visitor-shared/components/GhostLoading/GhostBlock";
import { GhostText } from "@faire/retailer-visitor-shared/components/GhostLoading/GhostTypography";
import { LoadingProductTileSkeleton } from "@faire/retailer-visitor-shared/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import {
  MAX_CONTENT_WIDTH,
  MAX_WIDE_SCREEN_CONTENT_WIDTH,
} from "@faire/retailer-visitor-shared/features/Marketplace/styleConsts";
import { OuterResultsContentWrapper } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/CategoryPageCommonStyles";
import { ElevatedContentWrapper } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/LoggedInCategoryPageStyles";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import React, { Fragment } from "react";
import styled, { css } from "styled-components";

const FILTER_PROPS = {
  $height: "40px",
  $borderRadius: "20px",
};

const NUM_TILES = {
  mobile: 4,
  tablet: 12,
  desktopAndAbove: 16,
};

const NUM_LONG_FILTERS = {
  mobile: 1,
  tablet: 3,
  desktopAndAbove: 4,
};

const NUM_FILTER_SECTIONS = 3;
const NUM_SIDENAV_CATEGORIES = 10;

export const LoggedInCategoryPageLoadingSkeleton: React.FC<{
  isFullWidthCategoryPage?: boolean;
}> = ({ isFullWidthCategoryPage }) => {
  return (
    <Container data-test-id="logged-in-category-page-loading-skeleton">
      <ElevatedContentWrapper
        $maxWidth={
          isFullWidthCategoryPage
            ? MAX_WIDE_SCREEN_CONTENT_WIDTH
            : MAX_CONTENT_WIDTH
        }
      >
        {/* Header */}
        <GhostBlock $width={getSpacing(160)} $height={getSpacing("5x")} />
        <Spacer size="1x" />
        <GhostBlock $width="50%" $height={getSpacing(20)} />
        <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "4x" }} />

        {/* Placeholder for a layout element */}
        <GhostBlock $width="100%" $height={getSpacing(280)} />
        <Spacer height={{ mobileAndAbove: "4x", desktopAndAbove: "5x" }} />

        {/* Sidebar & filters */}
        <ContentContainer>
          <SidebarContainerWithFilters>
            <GhostText />
            <Spacer width="2x" />
            <Column style={{ paddingLeft: 16 }}>
              {Array.from({ length: NUM_SIDENAV_CATEGORIES }).map(
                (_, index) => (
                  <Fragment key={index}>
                    <FilterPlaceholder />
                    <Spacer width="2x" />
                  </Fragment>
                )
              )}
            </Column>
            <HorizontalLine />
            {Array.from({ length: NUM_FILTER_SECTIONS }).map((_, index) => (
              <Fragment key={index}>
                <Spacer width="3x" />
                <GhostText />
                <Spacer width="3x" />
                <HorizontalLine />
              </Fragment>
            ))}
          </SidebarContainerWithFilters>
          <OuterResultsContentWrapper>
            {/* Header */}
            <GhostBlock $width={getSpacing(160)} $height={getSpacing("5x")} />
            <Spacer size="1x" />
            <GhostBlock $width="50%" $height={getSpacing(20)} />
            <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "4x" }} />
            {/* Filter row */}
            <FilterRow>
              <GhostBlock $width="44px" {...FILTER_PROPS} />
              <Spacer width="2x" />
              <GhostBlock $width="44px" {...FILTER_PROPS} />
              {Array.from({ length: NUM_LONG_FILTERS["desktopAndAbove"] }).map(
                (_, index) => (
                  <Fragment key={index}>
                    <Spacer width="2x" />
                    <LongFilter {...FILTER_PROPS} index={index} />
                  </Fragment>
                )
              )}
            </FilterRow>
            <Spacer height="2x" />
            {/* Tiles */}
            <TileGrid $isFullWidth={isFullWidthCategoryPage}>
              {Array.from({ length: NUM_TILES["desktopAndAbove"] }).map(
                (_, index) => (
                  <LoadingProductTileSkeleton key={index} />
                )
              )}
            </TileGrid>
          </OuterResultsContentWrapper>
        </ContentContainer>
      </ElevatedContentWrapper>
    </Container>
  );
};

const Container = styled(Column)`
  padding: ${getSpacing("4x", 0)};
`;

const TileGrid = styled.div<{ $isFullWidth?: boolean }>`
  display: grid;
  grid-gap: ${getSpacing("2x")};

  ${responsiveMediaQueries.mobile} {
    grid-template-columns: repeat(2, 2fr);
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    grid-template-columns: repeat(4, 4fr);
  }

  ${(props) =>
    props.$isFullWidth
      ? css`
          ${responsiveMediaQueries.xLargeAndAbove} {
            grid-template-columns: repeat(5, 5fr);
          }
        `
      : undefined}
`;

const hiddenStyles = css`
  position: absolute;
  z-index: -1;
`;

const FilterRow = styled(Row)`
  overflow-x: hidden;
`;

const LongFilter = styled(GhostBlock).attrs({
  $width: "120px",
})<{ index: number }>`
  ${responsiveMediaQueries.mobile} {
    ${({ index }) =>
      index > NUM_LONG_FILTERS["mobile"] - 1 ? hiddenStyles : ""}
  }

  ${responsiveMediaQueries.tablet} {
    ${({ index }) =>
      index > NUM_LONG_FILTERS["tablet"] - 1 ? hiddenStyles : ""}
  }
`;

const ContentContainer = styled(Row)`
  padding-top: 12px;
  height: 100%;
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const SidebarContainerWithFilters = styled(Column).attrs({ flex: "0 0 228px" })`
  margin-right: ${getSpacing("6x")};

  &&& {
    ${responsiveMediaQueries.tabletAndBelow} {
      display: none;
    }
  }
`;

const FilterPlaceholder = styled(GhostText)`
  padding: 10px;
`;
